import { appAlert } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import QRCode from "qrcode";
import { displayName, useMaskNames } from "./nameMask";
const API = apiUrl("");

/**
 * Manage staff — branches on the left, their people on the right.
 *
 * Staff records live in each branch's own database, so the branch has to be
 * chosen before anything can be listed. Making that the left-hand panel rather
 * than a dropdown keeps it visible: the question "who works where" is the one
 * being answered, and a dropdown hides the answer as soon as it is chosen.
 *
 * People are grouped by role because that is how a rota is thought about, and
 * because an ungrouped list of thirty names sorted alphabetically tells you
 * nothing about whether a branch is short of a dispenser.
 */
const ManageStaff = ({
  onClose
}) => {
  const masked = useMaskNames();
  const [branches, setBranches] = useState([]);
  const [selected, setSelected] = useState(null); // dbName
  const [data, setData] = useState(null); // { staff, roles, branch }
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [edit, setEdit] = useState(null); // staff row being edited
  const [qr, setQr] = useState(null); // { name, branch, dataUrl }
  const [showInactive, setShowInactive] = useState(true);
  const token = localStorage.getItem("authToken");
  const post = useCallback(async (path, body = {}) => {
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
    return j;
  }, [token]);
  useEffect(() => {
    post("/api/staffAdmin/branches").then(d => {
      setBranches(d.branches || []);
      if ((d.branches || []).length === 1) setSelected(d.branches[0].dbName);
    }).catch(e => setError(e.message));
  }, [post]);
  const loadStaff = useCallback(async dbName => {
    if (!dbName) return;
    setLoading(true);
    setError(null);
    try {
      setData(await post("/api/staffAdmin/staff", {
        dbName
      }));
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [post]);
  useEffect(() => {
    if (selected) loadStaff(selected);
  }, [selected, loadStaff]);
  const act = async (label, fn) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  /** Grouped by role, inactive people last within each group. */
  const grouped = useMemo(() => {
    const rows = (data?.staff || []).filter(s => showInactive || s.active);
    const map = new Map();
    for (const s of rows) {
      const key = s.role || "No role assigned";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    for (const list of map.values()) {
      list.sort((a, b) => (b.active ? 1 : 0) - (a.active ? 1 : 0) || a.name.localeCompare(b.name));
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [data, showInactive]);
  const showQr = s => act("qr", async () => {
    const d = await post("/api/staffAdmin/qr", {
      dbName: selected,
      staffId: s._id
    });
    const dataUrl = await QRCode.toDataURL(d.payload, {
      width: 260,
      margin: 2
    });
    setQr({
      name: d.name,
      branch: d.branch,
      dataUrl
    });
  });
  const S = {
    btn: bg => ({
      background: bg || "#fff",
      color: bg ? "#fff" : "#262626",
      border: "1px solid #D9D9D9",
      borderRadius: 4,
      padding: "5px 10px",
      marginRight: 6,
      cursor: "pointer",
      fontSize: 12.5
    }),
    th: {
      textAlign: "left",
      padding: "5px 8px",
      fontSize: 11,
      color: "#5D5D5D"
    },
    td: {
      padding: "5px 8px",
      fontSize: 12.5
    },
    input: {
      padding: 5,
      border: "1px solid #D9D9D9",
      borderRadius: 4,
      fontSize: 12.5
    }
  };
  return <div onClick={onClose} style={{
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200
  }}>
      <div onClick={e => e.stopPropagation()} style={{
      background: "#fff",
      borderRadius: 8,
      width: "min(1100px, 94vw)",
      height: "min(720px, 90vh)",
      display: "flex",
      flexDirection: "column"
    }}>

        <div style={{
        padding: "12px 16px",
        borderBottom: "1px solid #F0F0F0",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
          <b style={{
          fontSize: 16
        }}>Manage staff</b>
          <button style={S.btn()} onClick={onClose}>Close</button>
        </div>

        {error && <div style={{
        background: "#fff4f4",
        borderBottom: "1px solid #e0b4b4",
        padding: "8px 16px",
        fontSize: 12.5
      }}>{error}</div>}

        <div style={{
        flex: 1,
        display: "flex",
        minHeight: 0
      }}>

          {/* ----------------------------------------------- the branches */}
          <div style={{
          flex: "0 0 240px",
          borderRight: "1px solid #F0F0F0",
          overflowY: "auto"
        }}>
            <div style={{
            padding: "8px 12px",
            fontSize: 11,
            color: "#5D5D5D",
            borderBottom: "1px solid #F5F5F5"
          }}>
              PHARMACIES ({branches.length})
            </div>
            {branches.map(b => <div key={b.dbName} onClick={() => {
            setSelected(b.dbName);
            setEdit(null);
          }} style={{
            padding: "9px 12px",
            cursor: "pointer",
            fontSize: 13,
            borderBottom: "1px solid #FAFAFA",
            background: selected === b.dbName ? "#e8f4ef" : "transparent",
            borderLeft: selected === b.dbName ? "4px solid #177245" : "4px solid transparent"
          }}>
                {displayName(b.pharmacyName, masked)}
                {!b.pharmacyUid && <div style={{
              fontSize: 10.5,
              color: "#a05a00"
            }}>
                    no sign-in code available
                  </div>}
              </div>)}
          </div>

          {/* -------------------------------------------------- the staff */}
          <div style={{
          flex: 1,
          overflowY: "auto",
          padding: 12
        }}>
            {!selected && <div style={{
            color: "#5D5D5D",
            padding: 12
          }}>
                Choose a pharmacy to see its staff.
              </div>}
            {selected && loading && <div style={{
            padding: 12
          }}>Loading…</div>}

            {selected && !loading && data && <>
                <div style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 10,
              flexWrap: "wrap"
            }}>
                  <b style={{
                fontSize: 14
              }}>
                    {displayName(data.branch.pharmacyName, masked)}
                  </b>
                  <span style={{
                fontSize: 12,
                color: "#5D5D5D"
              }}>
                    {data.staff.length} on file
                  </span>
                  <label style={{
                fontSize: 12,
                color: "#5D5D5D"
              }}>
                    <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
                    {" "}show inactive
                  </label>
                  <button style={{
                ...S.btn("#177245"),
                marginLeft: "auto"
              }} onClick={() => setEdit({
                _id: null,
                name: "",
                email: "",
                roleId: "",
                active: true
              })}>
                    + Add staff member
                  </button>
                </div>

                {/* the editor, shown in place rather than as a second dialogue */}
                {edit && <div style={{
              border: "1px solid #BAE7FF",
              background: "#E6F7FF",
              borderRadius: 6,
              padding: 10,
              marginBottom: 12
            }}>
                    <div style={{
                fontSize: 12.5,
                fontWeight: "bold",
                marginBottom: 8
              }}>
                      {edit._id ? "Edit staff member" : "New staff member"}
                    </div>
                    <div style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "flex-end"
              }}>
                      <label style={{
                  fontSize: 12
                }}>Name
                        <input value={edit.name} autoFocus style={{
                    ...S.input,
                    display: "block",
                    width: 190
                  }} onChange={e => setEdit({
                    ...edit,
                    name: e.target.value
                  })} />
                      </label>
                      <label style={{
                  fontSize: 12
                }}>Email
                        <input value={edit.email} style={{
                    ...S.input,
                    display: "block",
                    width: 220
                  }} onChange={e => setEdit({
                    ...edit,
                    email: e.target.value
                  })} />
                      </label>
                      <label style={{
                  fontSize: 12
                }}>Role
                        <select value={edit.roleId || ""} style={{
                    ...S.input,
                    display: "block",
                    width: 170
                  }} onChange={e => setEdit({
                    ...edit,
                    roleId: e.target.value
                  })}>
                          <option value="">No role</option>
                          {(data.roles || []).map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                        </select>
                      </label>
                      <label style={{
                  fontSize: 12
                }}>
                        <input type="checkbox" checked={edit.active !== false} onChange={e => setEdit({
                    ...edit,
                    active: e.target.checked
                  })} />
                        {" "}Active
                      </label>
                      <div>
                        <button style={S.btn("#389E0D")} disabled={busy === "save" || !edit.name.trim()} onClick={() => act("save", async () => {
                    const r = await post("/api/staffAdmin/saveStaff", {
                      dbName: selected,
                      staffId: edit._id,
                      name: edit.name,
                      email: edit.email,
                      roleId: edit.roleId || null,
                      active: edit.active !== false
                    });
                    setEdit(null);
                    await loadStaff(selected);
                    if (r.pin) {
                      // The PIN is generated server-side and is the only
                      // time it is shown plainly, so it is stated rather
                      // than left to be discovered.
                      (await appAlert(`Added. Their sign-in PIN is ${r.pin}.`));
                    }
                  })}>
                          {busy === "save" ? "Saving…" : "Save"}
                        </button>
                        <button style={S.btn()} onClick={() => setEdit(null)}>Cancel</button>
                      </div>
                    </div>
                  </div>}

                {grouped.map(([role, list]) => <div key={role} style={{
              marginBottom: 14
            }}>
                    <div style={{
                fontSize: 12,
                fontWeight: "bold",
                color: "#177245",
                borderBottom: "2px solid #F0F0F0",
                paddingBottom: 3,
                marginBottom: 4
              }}>
                      {role} <span style={{
                  fontWeight: "normal",
                  color: "#5D5D5D"
                }}>
                        · {list.length}
                      </span>
                    </div>
                    <table style={{
                width: "100%",
                borderCollapse: "collapse"
              }}>
                      <tbody>
                        {list.map(s => <tr key={s._id} style={{
                    borderBottom: "1px solid #FAFAFA",
                    opacity: s.active ? 1 : 0.5
                  }}>
                            <td style={{
                      ...S.td,
                      fontWeight: "bold"
                    }}>
                              {displayName(s.name, masked)}
                              {!s.active && <span style={{
                        fontWeight: "normal",
                        color: "#CF1322",
                        fontSize: 11
                      }}> · inactive</span>}
                            </td>
                            <td style={{
                      ...S.td,
                      color: "#5D5D5D"
                    }}>{s.email}</td>
                            <td style={{
                      ...S.td,
                      textAlign: "right",
                      whiteSpace: "nowrap"
                    }}>
                              <button style={{
                        ...S.btn(),
                        padding: "2px 8px",
                        fontSize: 11
                      }} onClick={() => setEdit({
                        ...s
                      })}>Edit</button>
                              <button style={{
                        ...S.btn(),
                        padding: "2px 8px",
                        fontSize: 11
                      }} disabled={busy === "qr"} onClick={() => showQr(s)}>View QR code</button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>)}

                {grouped.length === 0 && <div style={{
              color: "#5D5D5D",
              padding: 12
            }}>
                    Nobody on file for this pharmacy yet.
                  </div>}
              </>}
          </div>
        </div>
      </div>

      {/* --------------------------------------------------- the QR code */}
      {qr && <div onClick={e => {
      e.stopPropagation();
      setQr(null);
    }} style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.55)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 210
    }}>
          <div onClick={e => e.stopPropagation()} style={{
        background: "#fff",
        borderRadius: 8,
        padding: 20,
        textAlign: "center"
      }}>
            <div style={{
          fontSize: 17,
          fontWeight: "bold",
          marginBottom: 4
        }}>
              {displayName(qr.name, masked)}
            </div>
            <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginBottom: 12
        }}>
              {displayName(qr.branch, masked)}
            </div>
            <img src={qr.dataUrl} alt="Sign-in code" style={{
          display: "block",
          margin: "0 auto"
        }} />
            <div style={{
          marginTop: 14
        }}>
              <button style={S.btn()} onClick={() => window.print()}>Print</button>
              <button style={S.btn("#0958D9")} onClick={() => setQr(null)}>Close</button>
            </div>
          </div>
        </div>}
    </div>;
};
export default ManageStaff;
